import Seo, { SITE } from '../../components/Seo/Seo';

// Reuse the /terms (Policies) brand-card shell so the two legal pages match;
// PrivacyPolicy.css adds only the flat-prose body styles (h2/p/ul) since a
// policy reads top-to-bottom instead of the Policies accordion.
import '../Policies/Policies.css';
import './PrivacyPolicy.css';

// Public, indexable legal page. Copy is transcribed VERBATIM from the canonical
// draft at Drive: CYV-Operations/reference/cyv-privacy-policy.md — that file
// stays canonical. If the policy text changes, update BOTH the Drive file and
// this page.
//
// EFFECTIVE_DATE is the go-live date, set at publish. Keep it in sync with the
// Drive canonical's "Effective date:" line if the policy is ever re-published.
const EFFECTIVE_DATE = 'July 3, 2026';

const PrivacyPolicy = () => {
	return (
		<main className='cyv policies-page'>
			<Seo
				title='Privacy Policy | Color Your Voice'
				description='How Color Your Voice collects, uses, and protects the information families share with us at coloryourvoice.com.'
				canonical={`${SITE}/privacy`}
			/>

			<div className='pol-wrap pol'>
				<div className='pol-card'>
					<div className='pol-card__head'>PRIVACY POLICY</div>
					<div className='pol-card__body'>
						<div className='legal-prose'>
							<p className='legal-org'>
								<strong>Color Your Voice, LLC</strong>
							</p>
							<p className='legal-effective'>
								<em>Effective date: {EFFECTIVE_DATE}</em>
							</p>

							<p>
								Color Your Voice (&ldquo;Color Your Voice,&rdquo;
								&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) is
								a youth voice and performing arts studio based in Burnsville,
								Minnesota. We care about the families who trust us with their
								children, and that includes being clear about the information
								we collect and how we use it. This policy explains what we
								gather when you visit coloryourvoice.com or reach out to us,
								why we collect it, and the choices you have.
							</p>
							<p>
								We keep this short and plain on purpose. If anything here is
								unclear, please email us at{' '}
								<a href='mailto:sing@coloryourvoice.com'>
									sing@coloryourvoice.com
								</a>{' '}
								and we&rsquo;ll be glad to explain.
							</p>

							<h2>Who this policy covers</h2>
							<p>
								This policy applies to our website, coloryourvoice.com, and
								to the messages and registrations families send us through
								it. It is written for the parents and guardians who contact
								us and enroll their children. Students at Color Your Voice are
								typically minors, and the information we hold about a student
								is provided to us by that student&rsquo;s parent or guardian
								&mdash; we do not ask children to submit their own
								information through our site (see &ldquo;Children&rsquo;s
								privacy&rdquo; below).
							</p>

							<h2>Information we collect</h2>
							<p>
								<strong>Information you give us directly.</strong> When you
								use our contact form, we collect the name, email address,
								optional phone number, and the message you send. When you
								purchase a lesson pack or register for a workshop, we collect
								the parent or guardian&rsquo;s name, email, and phone number,
								the student&rsquo;s name, the student&rsquo;s age (optional),
								and any goals or notes you choose to share. If you enroll for
								ongoing lessons, we also collect the scheduling and account
								information needed to run those lessons.
							</p>
							<p>
								<strong>Payment information.</strong> We do not collect or
								store your card number. When you buy a lesson pack or
								workshop, you are handed off to Stripe, our payment
								processor, and you enter your card details on Stripe&rsquo;s
								own secure checkout page. We receive a confirmation that a
								payment succeeded and the amount paid, but not your full card
								details.
							</p>
							<p>
								<strong>Information collected automatically.</strong> Like
								most websites, we collect some basic technical information
								when you visit &mdash; things like your device and browser
								type, general location inferred from your IP address, the
								pages you view, and how you arrived at our site. We collect
								this through cookies and similar technologies, described in
								&ldquo;Cookies and tracking&rdquo; below.
							</p>

							<h2>How we use your information</h2>
							<p>We use the information we collect to:</p>
							<ul>
								<li>
									respond to your questions and follow up about lessons,
									workshops, and scheduling;
								</li>
								<li>
									process lesson-pack and workshop purchases and send you a
									confirmation;
								</li>
								<li>
									set up and manage enrolled students&rsquo; lessons,
									billing, and account access;
								</li>
								<li>run, secure, and improve our website;</li>
								<li>
									measure how our advertising performs, so we can reach
									families who might be a good fit; and
								</li>
								<li>
									send you information you asked for, such as details about
									ongoing school-year lessons if you opted in.
								</li>
							</ul>

							<h2>How we share your information</h2>
							<p>
								We do not sell your personal information. We share it only
								with the service providers that help us run the studio, and
								only as needed:
							</p>
							<ul>
								<li>
									<strong>Stripe</strong> processes payments. Your payment
									details go directly to Stripe under its own privacy policy.
								</li>
								<li>
									<strong>Google Firebase</strong> (Google Cloud) hosts our
									website and stores the inquiries, registrations, and
									account records described above.
								</li>
								<li>
									<strong>Google reCAPTCHA</strong> helps us keep spam and
									automated abuse off our forms; Google may collect device
									and usage information for that purpose.
								</li>
								<li>
									<strong>MyMusicStaff</strong> provides the scheduling and
									family-billing portal we use for enrolled students.
								</li>
								<li>
									<strong>Meta (Facebook and Instagram)</strong> receives
									information through our advertising pixel about actions
									taken on our site, such as page views, inquiries, and
									purchases, so we can measure and improve our ads. See
									&ldquo;Cookies and tracking&rdquo; for how to limit this.
								</li>
								<li>
									<strong>Email and communication tools</strong> we use to
									reply to you and send confirmations.
								</li>
							</ul>
							<p>
								We may also share information if the law requires it, to
								protect the safety or rights of our families, staff, or
								studio, or in connection with a sale or reorganization of the
								business.
							</p>

							<h2>Cookies and tracking</h2>
							<p>We use a small number of cookies and similar technologies:</p>
							<ul>
								<li>
									<strong>Essential and functional</strong> &mdash; needed
									for the site to work and, for enrolled families, to keep
									you signed in to the portal.
								</li>
								<li>
									<strong>Security</strong> &mdash; Google reCAPTCHA, to
									protect our forms from spam and abuse.
								</li>
								<li>
									<strong>Advertising and measurement</strong> &mdash; the
									Meta Pixel, which reports actions on our site back to Meta
									so we can measure and target our ads.
								</li>
							</ul>
							<p>
								You can control cookies through your browser settings, and
								you can limit ad tracking in a few ways: adjust your ad
								preferences within Facebook and Instagram, use your
								device&rsquo;s ad-tracking controls, and use industry opt-out
								tools such as the ones at optout.aboutads.info and
								youradchoices.com. Blocking some cookies may affect how parts
								of the site work.
							</p>

							<h2>Children&rsquo;s privacy</h2>
							<p>
								Color Your Voice teaches children, but our website is
								directed to parents and guardians, not to children. We do not
								knowingly collect personal information directly from a child.
								The information we hold about a student &mdash; name, age,
								goals &mdash; is provided to us by that child&rsquo;s parent
								or guardian when they contact us or register. If you believe
								a child has provided us information directly, please email{' '}
								<a href='mailto:sing@coloryourvoice.com'>
									sing@coloryourvoice.com
								</a>{' '}
								and we will delete it.
							</p>

							<h2>How long we keep information</h2>
							<p>
								We keep your information for as long as we need it to serve
								your family and to meet our business, tax, and legal
								obligations. When we no longer need it, we delete it or
								remove the details that identify you. If you ask us to delete
								your information, we will do so unless we are required to keep
								some of it (for example, records of completed payments).
							</p>

							<h2>Your choices</h2>
							<p>You can:</p>
							<ul>
								<li>
									<strong>Ask what we have.</strong> Email us and we&rsquo;ll
									tell you what information we hold about you.
								</li>
								<li>
									<strong>Correct or update it.</strong> Let us know and
									we&rsquo;ll fix it.
								</li>
								<li>
									<strong>Ask us to delete it.</strong> We&rsquo;ll delete
									what we can and explain anything we&rsquo;re required to
									keep.
								</li>
								<li>
									<strong>Opt out of marketing.</strong> Any marketing email
									includes an unsubscribe link, and you can limit ad tracking
									as described above.
								</li>
							</ul>
							<p>
								To make any of these requests, email{' '}
								<a href='mailto:sing@coloryourvoice.com'>
									sing@coloryourvoice.com
								</a>
								. We&rsquo;ll respond as promptly as we reasonably can.
							</p>

							<h2>Data security</h2>
							<p>
								We use reputable providers (Stripe, Google, MyMusicStaff) and
								reasonable safeguards to protect your information. No method
								of storing or transmitting data online is perfectly secure,
								so we can&rsquo;t guarantee absolute security, but we work to
								keep your information safe and to limit who can see it.
							</p>

							<h2>Links to other sites</h2>
							<p>
								Our site may link to other websites, such as our payment page
								on Stripe or our family portal. Those sites have their own
								privacy policies, and this policy doesn&rsquo;t cover them.
							</p>

							<h2>Changes to this policy</h2>
							<p>
								We may update this policy as our studio and our tools change.
								When we do, we&rsquo;ll update the effective date at the top.
								Meaningful changes will be posted here, and we encourage you
								to check back from time to time.
							</p>

							<h2>Contact us</h2>
							<p>
								Questions about this policy or your information? We&rsquo;re
								happy to help.
							</p>
							<p className='legal-org'>
								<strong>Color Your Voice, LLC</strong>
								<br />
								12004 12th Ave S
								<br />
								Burnsville, MN 55337
								<br />
								Email:{' '}
								<a href='mailto:sing@coloryourvoice.com'>
									sing@coloryourvoice.com
								</a>
								<br />
								Phone: <a href='tel:9522830560'>952-283-0560</a>
							</p>

							<hr className='legal-rule' />

							<p className='legal-footnote'>
								<em>
									This policy works alongside our{' '}
									<a href='/terms'>Terms and Policies</a>.
								</em>
							</p>
						</div>
					</div>
				</div>
			</div>
		</main>
	);
};

export default PrivacyPolicy;
